import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Card, CardFooter, CardPreview, Field, Image, ProgressBar, makeStyles } from "@fluentui/react-components";
import Preview from "../assets/splash.png";

const StyledLoading = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: ${props => (props.fullScreen ? "100vh" : "100%")};
  width: ${props => (props.fullScreen ? "100vw" : "100%")};
  min-height: 300px;
  position: fixed;
  z-index: 5;
  background-color: var(--primaryColorBackground);
  overflow: hidden;

  svg {
    margin-bottom: 20px;
  }
`;

const useStyles = makeStyles({
  card: {
    margin: "auto",
    width: "500px",
    maxWidth: "90%",
    "@media (max-width: 768px)": {
      width: "100%",
      maxWidth: "95%"
    }
  },
  cardPreview: {
    maxHeight: "600px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  image: {
    width: "100%",
    height: "auto",
    maxHeight: "600px",
    objectFit: "cover",
    "@media (max-width: 768px)": {
      maxHeight: "400px"
    },
    "@media (max-width: 480px)": {
      maxHeight: "300px"
    }
  },
  cardFooter: {
    width: "100%",
    backgroundColor: "rgba(20, 20, 20, 1)"
  },
  creditsText: {
    position: "absolute",
    bottom: "12px",
    left: "-12px",
    color: "#fff",
    background: "linear-gradient(to right, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.1))",
    padding: "4px 10px",
    fontSize: "10px",
    zIndex: 2,
    maxWidth: "70%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "@media (max-width: 768px)": {
      fontSize: "9px",
      maxWidth: "60%",
      padding: "3px 8px"
    },
    "@media (max-width: 480px)": {
      fontSize: "8px",
      maxWidth: "50%",
      padding: "2px 6px"
    }
  },
  versionText: {
    position: "absolute",
    bottom: "12px",
    right: "-12px",
    color: "white",
    background: "linear-gradient(to left, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.1))",
    padding: "0px 8px",
    fontSize: "10px",
    fontWeight: "500",
    "@media (max-width: 768px)": {
      fontSize: "9px",
      padding: "0px 6px"
    },
    "@media (max-width: 480px)": {
      fontSize: "8px",
      padding: "0px 4px"
    }
  }
});

const PLATFORM_VERSION = "v1.2.6";

const InitialLoading = ({ fullScreen, api }) => {
  const classes = useStyles();
  const [isImageLoaded, setImageLoaded] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [loadingSplash, setLoadingSplash] = useState(Preview);
  const [loadingSplashCredits, setLoadingSplashCredits] = useState("Giulio Romano, Sala dei Giganti by Zeuxis VR");
  const [appName, setAppName] = useState("XRHUB");

  useEffect(() => {
    const fetchSiteSettingsData = async () => {
      if (!api) {
        setIsDataLoaded(true);
        return;
      }

      try {
        const siteSettings = await api.getPlatformSiteSettings();
        if (siteSettings?.branding) {
          const { branding } = siteSettings;
          const loadingBranding = branding.splash?.loading;

          if (loadingBranding?.image) {
            setLoadingSplash(loadingBranding.image);
          }
          if (loadingBranding?.credits) {
            setLoadingSplashCredits(loadingBranding.credits);
          }
          if (branding.identity?.name) {
            setAppName(branding.identity.name);
          }
        }
      } catch (error) {
        console.error("Error fetching site settings data:", error);
        // Continue with default values if fetch fails
      } finally {
        setIsDataLoaded(true);
      }
    };

    fetchSiteSettingsData();
  }, [api]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const showCard = isImageLoaded && isDataLoaded;

  return (
    <StyledLoading fullScreen={fullScreen}>
      <Card className={classes.card} style={{ visibility: showCard ? "visible" : "hidden" }}>
        {isDataLoaded && (
          <CardPreview className={classes.cardPreview}>
            <Image src={loadingSplash} alt="Logo" onLoad={handleImageLoad} className={classes.image} />
          </CardPreview>
        )}
        <div style={{ position: "relative" }}>
          {loadingSplashCredits && <div className={classes.creditsText}>{loadingSplashCredits}</div>}
          <div className={classes.versionText}>{PLATFORM_VERSION}</div>
        </div>
        <Field label={appName} size="large"></Field>
        <Field validationMessage="Starting... get ready to be amazed!" validationState="none">
          <ProgressBar thickness="large" />
        </Field>

        <CardFooter></CardFooter>
      </Card>
    </StyledLoading>
  );
};

InitialLoading.propTypes = {
  message: PropTypes.string,
  fullScreen: PropTypes.bool,
  api: PropTypes.object
};

export default InitialLoading;
